import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Alert,
    Anchor,
    Code,
    Group,
    List,
    Stack,
    Text,
} from '@mantine/core';
import {FlexiCopyButton} from '@flexinet/ui-components';

import {ProviderConnection} from '../types';
import {CONNECT_PROVIDER_DOCS_URL} from '../config';
import {
    presentStatus,
    rendersExternalIdPanel,
} from '../statusPresentation';

// FNP-1248 — THE EXTERNAL ID, AND WHY THE CONNECTION IS NOT ACTIVE YET.
//
// ⚠️ THIS COMPONENT IS THE POINT OF THE WHOLE MODULE AT STEP 2 OF 3.
//
// The ordering problem it exists to solve:
//
//   Flexi mints a per-connection external id SERVER-SIDE during
//   `connectProvider`, via issueExternalId(connectionId). It is DERIVED FROM
//   THE CONNECTION ID, so it cannot exist before the connection does. The
//   tenant needs it in their role's trust policy before Flexi can assume the
//   role. Therefore:
//
//     1. Connect      → connection stored, status INVALID/UNREACHABLE,
//                       secretRef.externalId now populated.
//     2. THIS PANEL   → show the external id + role ARN so the tenant can add
//                       them to their trust policy.
//     3. Re-validate  → rotateProviderCredential with the same roleArn.
//
// Step 1 coming back not-ACTIVE is the DESIGNED path. There is no ordering in
// which a first Route 53 connection succeeds immediately, and no amount of
// server-side cleverness can produce one — the id is a function of the record.
//
// ⚠️ `testProviderCredential` CANNOT SHORTCUT THIS. The external id it mints is
// a throwaway: never stored, never returned. A tenant cannot put it in a trust
// policy, so it can never make an assumption succeed. Nothing in this module
// implies otherwise.
//
// ⚠️ WHAT IS SHOWN AND WHAT IS NOT.
//   externalId — SHOWN, prominently, copyable. Flexi must TELL the tenant this.
//   roleArn    — SHOWN when known. An identifier, not a secret; the tenant
//                needs it to find the right role.
//   secretArn  — NEVER. Infrastructure detail with no tenant audience; it is
//                not even selected in this module's queries.
//   apiToken   — NEVER, and structurally cannot be: no output type carries it.

export const ExternalIdPanel = (
    {
        connection,
        roleArn,
    }: {
        connection: ProviderConnection,
        // The role ARN the tenant submitted, when the caller still has it in
        // hand — right after a connect or rotate. It is NOT stored on the
        // connection record, so on a page load it is legitimately absent and
        // the panel simply omits that line rather than inventing one.
        roleArn?: string,
    },
) => {

    const externalId = connection.secretRef?.externalId;

    // No external id means this is not the ASSUMED_ROLE trust-policy flow —
    // an API_TOKEN connection, or an ASSUMED_ROLE one the API did not mint an
    // id for. Either way there is nothing here to tell the tenant to do.
    //
    // ⚠️ GATED ON THE SHARED HELPER, not on a second reading of `secretRef`.
    // The status card and the connect drawer both stand their own explanation
    // down when `rendersExternalIdPanel` says this panel will render (FNP-1286).
    // If that helper and this early return ever disagreed, a state would be
    // explained twice or not at all — so they are the same predicate, not two
    // copies of one rule. `externalId` is still narrowed below for the type.
    if (!rendersExternalIdPanel(connection) || !externalId) {
        return null;
    }

    const presentation = presentStatus(connection);

    return (
        <Alert
            data-testid={'external-id-panel'}
            // ⚠️ BLUE FOR THE EXPECTED STEP, not red. This is an instruction,
            // not a failure report. A red panel here tells the tenant they made
            // a mistake at the exact moment they need to calmly copy a value.
            color={presentation.isExpectedNextStep ? 'blue' : 'gray'}
            variant={'light'}
            title={presentation.isExpectedNextStep
                ? I18n.get('Next step: add this external ID to your role\'s trust policy')
                : I18n.get('External ID for this connection')}
        >
            <Stack gap={'md'}>

                {/* ⚠️ FNP-1286 — THE SUMMARY RENDERS HERE, AND ONLY HERE, FOR
                    EVERY STATE THIS PANEL IS ON SCREEN FOR.
                    The detail view's status card used to render it too, so the
                    tenant read the same paragraph twice. The card now stands
                    down whenever this panel renders, which means this panel
                    must carry the sentence in EVERY state, not just the
                    awaiting-trust-policy one — an ACTIVE connection keeps its
                    external id, so this panel renders for it as well, and
                    dropping the summary would leave that state explained by
                    nothing but a badge.

                    `presentation.summary` rather than a paraphrase: the one
                    sentence that stops a tenant reading INVALID as their own
                    mistake is then the same sentence everywhere it appears, and
                    cannot drift between here and the table. */}
                <Text size={'sm'} fw={presentation.isExpectedNextStep ? 500 : undefined}>
                    {presentation.summary}
                </Text>

                {presentation.isExpectedNextStep && (
                    <Text size={'sm'}>
                        {productCopy('{product} created this connection and generated an external ID for it. The ID is derived from the connection, so it could not be issued before now. Your role will not accept {product} until the ID is in its trust policy — that is why this connection is not active yet.')}
                    </Text>
                )}

                {/* ─── THE VALUE THE TENANT CAME FOR ─── */}
                <Stack gap={4}>
                    <Text size={'xs'} c={'dimmed'} fw={500}>
                        {I18n.get('External ID')}
                    </Text>
                    <Group gap={'xs'} wrap={'nowrap'}>
                        <Code data-testid={'external-id'}>{externalId}</Code>
                        {/* Copyable because it is a long opaque string that
                            must be transcribed EXACTLY into a trust policy.
                            Retyping it by hand is the likeliest way this flow
                            fails for a reason nobody can see. */}
                        {/* `data-testid` because this is an icon-only
                            ActionIcon inside a Tooltip: the "Copy" label lives
                            on the tooltip, which gives the button no accessible
                            name in jsdom, so `getByRole('button', {name: /copy/})`
                            cannot find it. The testid is how a test proves the
                            copy control survived (FNP-1286). */}
                        <FlexiCopyButton value={externalId} data-testid={'external-id-copy'}/>
                    </Group>
                </Stack>

                {roleArn && (
                    <Stack gap={4}>
                        <Text size={'xs'} c={'dimmed'} fw={500}>
                            {I18n.get('Role ARN')}
                        </Text>
                        <Group gap={'xs'} wrap={'nowrap'}>
                            <Code data-testid={'role-arn'}>{roleArn}</Code>
                            <FlexiCopyButton value={roleArn}/>
                        </Group>
                    </Stack>
                )}

                {presentation.isExpectedNextStep && (
                    <Stack gap={4}>
                        <Text size={'sm'} fw={500}>{I18n.get('What to do')}</Text>
                        <List size={'sm'} spacing={4}>
                            <List.Item>
                                {I18n.get('Open the IAM role in your own AWS account.')}
                            </List.Item>
                            <List.Item>
                                {I18n.get('Edit its trust policy so the sts:ExternalId condition matches the ID above.')}
                            </List.Item>
                            {/* ⚠️ FNP-1276 — THE PERMISSION POLICY, NOT ONLY THE
                                TRUST POLICY.

                                The trust policy decides WHO may assume the role.
                                It says nothing about what the role may then do,
                                and a tenant who fixes only the external id ends
                                up with a connection that assumes cleanly and
                                still cannot issue a certificate.

                                Decision 5 of docs/decisions/certificate-entity-model.md
                                puts the ACM grants in THIS role, deliberately:
                                the certificate is the tenant's asset, issued in
                                the tenant's account. Flexi holds no ACM write
                                grant anywhere, so there is nothing on Flexi's
                                side to fall back to.

                                The tenant is inside the role right now. This is
                                the cheapest moment in the whole flow to widen
                                the permission policy, and the only one before
                                the first request fails. */}
                            <List.Item>
                                {productCopy('While you are in the role, check its permission policy. Alongside the Route 53 actions, it needs acm:RequestCertificate and acm:DeleteCertificate to issue certificates through {product}. Certificates are issued in your own AWS account under this credential.')}
                            </List.Item>
                            <List.Item>
                                {I18n.get('Come back here and choose Re-validate. Your external ID does not change when you re-validate, so you only have to do this once.')}
                            </List.Item>
                        </List>
                        {/* ⚠️ THE GRANTS MUST OUTLIVE THE REQUEST.
                            AWS re-checks the DNS validation record for the life
                            of the certificate, so both the Route 53 write grant
                            and the ACM grants have to stay for years. A tenant
                            tightening the role months later breaks renewal, and
                            nothing links the two events — recorded as a real
                            trap in the decision doc's "Still open" section. It
                            is worth its own line rather than a clause tacked on
                            to a step the tenant has already ticked off. */}
                        <Text size={'xs'} c={'dimmed'} mt={4}>
                            {I18n.get('Leave these permissions in place afterwards. AWS re-checks the validation record for as long as the certificate lives, so removing them later breaks renewal silently.')}
                        </Text>
                        {/* FNP-1301 — the policies in full, as a page that can
                            be read before this panel or handed to whoever
                            administers the tenant's AWS account. */}
                        <Anchor
                            size={'xs'}
                            href={CONNECT_PROVIDER_DOCS_URL}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            mt={4}
                        >
                            {I18n.get('The full trust policy and permission policy')}
                        </Anchor>
                    </Stack>
                )}

                {/* The provider's own words, VERBATIM, when there are any.
                    Never re-worded — classify for the core, preserve for the
                    human (FNP-1180). Kept dimmed and below the instruction: it
                    is corroborating detail here, not the headline. */}
                {connection.statusDetail && (
                    <Stack gap={4}>
                        <Text size={'xs'} c={'dimmed'} fw={500}>
                            {I18n.get('Provider response')}
                        </Text>
                        <Text size={'xs'} c={'dimmed'}>
                            {connection.statusDetail}
                        </Text>
                    </Stack>
                )}

            </Stack>
        </Alert>
    );
};
